import ContinueButton from "@components/ContinueButton";
import Error from "@hudson/ui/components/Error";
import Button from "@hudson/ui/components/FormFields/Button";
import Email from "@hudson/ui/components/FormFields/Email";
import ConcentricLoader from "@hudson/ui/components/Loader/ConcentricLoader";
import { FormProvider, useForm } from "@hudson/ui/components/ReactHookForm";
import useConfig from "@lib/use_config";
import useContent from "@lib/use_content";
import profileAdd from "@services/profile_add";
import { useRouter } from "next/navigation";
import { useState } from "react";
import PasswordStrengthBar from "@components/PasswordStrengthBar";
import Name from "./shared/Name";
import NewPassword from "./shared/NewPassword";
import Username from "./shared/Username";

interface NewUserProps {
  onNewUser?: () => void;
  onPageChange?: (page: string) => void;
  groupId?: string;
}

function NewUser({ onNewUser, onPageChange, groupId }: NewUserProps) {
  const router = useRouter();
  const { data: configData, loading: configLoading } = useConfig(["enableWebIdForLogin"]);
  const { data: cData, loading: contentLoading } = useContent([
    "beErrEmailFormat",
    "beErrFieldRequired",
    "beErrProfileUpdate",
    "beNewProfileBackButton",
    "beNewProfileSubmitButton",
    "beNewProfileTitle",
    "beProfileEmailLabel",
    "beProfileNameLabel",
    "beProfilePasswordLabel",
    "beProfileUsernameLabel",
    "beRequiredPlaceholder",
  ]);
  const formMethods = useForm({});
  const [errorMessage, setErrorMessage] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const inputPassword = formMethods.watch("password");

  interface NewData {
    email: string;
    webId?: string;
    name?: string;
    password?: string;
  }

  async function submitForm(data: NewData) {
    setErrorMessage("");
    setSubmitting(true);

    const { error, profileId } = await profileAdd({
      ...data,
      groupId: groupId ? parseInt(groupId) : undefined,
    });

    if (!profileId) {
      setErrorMessage(error || cData?.content.beErrProfileUpdate);
    } else {
      // TODO: Legacy Rails client center app integration
      // Check if parent window has profileCreated function and execute it with the new profile ID
      if (window.parent && window.parent !== window) {
        try {
          // @ts-ignore - Legacy function may not be typed
          if (typeof window.parent.profileCreated === "function") {
            // @ts-ignore
            window.parent.profileCreated(profileId);
          }
        } catch (e) {
          // Cross-origin access may fail, ignore silently
        }
      }

      if (onNewUser) onNewUser();
      else router.push(`/${window.siteId}?open_profile=1`);
    }

    setSubmitting(false);
  }

  const loading = contentLoading || configLoading;
  const useWebId = configData?.config?.enableWebIdForLogin;

  return (
    <>
      {loading ? (
        <ConcentricLoader className="max-w-2xl px-4 mx-auto opacity-60 sm:py-10 sm:px-6 lg:px-8" />
      ) : (
        <FormProvider {...formMethods}>
          <form
            className="flex flex-col items-center max-w-xs m-6 mx-auto gap-y-6 sm:gap-x-4"
            id="new-user"
            onSubmit={(e) => {
              e.preventDefault();
              formMethods.handleSubmit(submitForm)();
            }}
            noValidate
          >
            {onPageChange && (
              <div className="self-start mb-4">
                <Button type="button" onClick={() => onPageChange("loginOrCreate")} asLink>
                  {cData?.content.beNewProfileBackButton}
                </Button>
              </div>
            )}

            <h2 className="flex flex-wrap items-center mb-3 text-3xl font-extrabold md:text-3xl opacity-70">
              {cData?.content.beNewProfileTitle}
            </h2>

            {errorMessage && <Error className="mt-2" message={errorMessage} heavyStyle scrollTo />}

            <>
              <Name
                autoFocus
                errorMessage={cData?.content.beErrFieldRequired}
                fieldName="name"
                label={cData.content.beProfileNameLabel}
                requiredPlaceholder={cData?.content.beRequiredPlaceholder}
              />

              {useWebId && (
                <Username
                  errorMessage={cData?.content.beErrFieldRequired}
                  fieldName="webId"
                  label={cData?.content.beProfileUsernameLabel}
                  requiredPlaceholder={cData?.content.beRequiredPlaceholder}
                />
              )}

              <Email
                errorMessage={cData?.content.beErrFieldRequired}
                errorFormatMessage={cData?.content.beErrEmailFormat}
                fieldName="email"
                label={cData.content.beProfileEmailLabel}
                requiredPlaceholder={cData?.content.beRequiredPlaceholder}
                widthClass="w-72"
              />

              {!groupId && (
                <>
                  <NewPassword
                    errorMessage={cData?.content.beErrFieldRequired}
                    fieldName="password"
                    label={cData?.content.beProfilePasswordLabel}
                    requiredPlaceholder={cData?.content.beRequiredPlaceholder}
                  />
                  <PasswordStrengthBar className="-mt-4 w-72" minLength={8} password={inputPassword} />
                </>
              )}

              <ContinueButton
                alignLeft
                className="mt-2 justify-self-start"
                disabled={submitting}
                label={cData?.content.beNewProfileSubmitButton}
                loading={submitting}
              />
            </>
          </form>
        </FormProvider>
      )}
    </>
  );
}

export default NewUser;
